import { CheckIcon, SpinnerGapIcon, XIcon } from '@phosphor-icons/react'
import type { ReactNode } from 'react'

import type { Tinte } from '@/components/Editorial'
import { AmountDisplay, NumPad } from '@/components/NumPad'
import { cn } from '@/lib/cn'
import { haptic } from '@/lib/haptics'

/**
 * Primitivos de formulario.
 *
 * Todos los formularios de la app comparten esta estructura: hoja a pantalla
 * completa con encabezado, contenido desplazable y una acción fija abajo. Es
 * deliberado — dar de alta una tarjeta y una meta de ahorro deben sentirse la
 * misma operación, aunque los campos sean distintos.
 *
 * El botón fijo abajo, y no al final del scroll, es lo que evita tener que
 * desplazarse a ciegas buscando dónde guardar.
 */

/**
 * El encabezado de la hoja lleva el tinte de la sección de la que sale.
 *
 * Una hoja a pantalla completa tapa la pantalla de origen, y con el encabezado
 * claro de siempre todas las hojas eran iguales: «Nuevo gasto fijo» y «Nueva
 * tarjeta» solo se distinguían por el título. Con el tinte, quien abre la hoja
 * sigue sabiendo en qué parte de la app está —durazno lo que sale, lavanda las
 * tarjetas, menta el ahorro, mantequilla lo que entra— sin leer nada. El neutro
 * conserva el filete porque sin tinte no hay otra cosa que lo separe.
 */
const ENCABEZADO: Record<Tinte, string> = {
  butter: 'bg-tint-butter',
  lavender: 'bg-tint-lavender',
  mint: 'bg-tint-mint',
  peach: 'bg-tint-peach',
  neutro: 'border-b border-line',
}

export function FormSheet({
  titulo,
  onCerrar,
  children,
  accion,
  tinte = 'neutro',
}: {
  titulo: string
  onCerrar: () => void
  children: ReactNode
  accion: ReactNode
  tinte?: Tinte
}) {
  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-bg">
      <header className={cn('px-safe flex items-center gap-3 pb-3 pt-safe', ENCABEZADO[tinte])}>
        <button
          type="button"
          onClick={() => {
            haptic('light')
            onCerrar()
          }}
          aria-label="Cancelar"
          className="flex size-11 shrink-0 items-center justify-center rounded-full text-ink-soft active:scale-95"
        >
          <XIcon size={20} />
        </button>
        <h1 className="truncate font-display text-[18px] font-bold tracking-[-0.02em] text-ink">
          {titulo}
        </h1>
      </header>

      <div className="scroll-contain px-safe flex-1 overflow-y-auto py-4">
        <div className="mx-auto flex max-w-lg flex-col gap-5">{children}</div>
      </div>

      <footer className="px-safe border-t border-line bg-surface pb-safe pt-3">
        <div className="mx-auto max-w-lg">{accion}</div>
      </footer>
    </div>
  )
}

export function Guardar({
  onClick,
  disabled = false,
  guardando = false,
  children = 'Guardar',
}: {
  onClick: () => void
  disabled?: boolean
  guardando?: boolean
  children?: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || guardando}
      className={cn(
        // Pastilla y no tarjeta: el radio completo es la forma del botón en toda
        // la app, y la que lo distingue de las cajas de contenido.
        'flex min-h-[52px] w-full items-center justify-center gap-2 rounded-pill text-base font-bold',
        'transition-transform duration-150 active:scale-[0.97]',
        disabled || guardando
          ? 'cursor-not-allowed bg-surface-sunken text-ink-faint'
          : 'bg-accion text-on-accion',
      )}
    >
      {guardando ? (
        <SpinnerGapIcon size={20} className="animate-spin" />
      ) : (
        <>
          <CheckIcon size={19} weight="bold" />
          {children}
          {/* El chevrón doble en mantequilla: la firma de la línea audaz y el
              único acento de marca que se gasta en un formulario. Se apaga
              cuando el botón está inhabilitado, porque un acento vivo sobre un
              botón muerto se lee como que sí se puede tocar. */}
          {!disabled ? (
            <span aria-hidden="true" className="text-[17px] font-black leading-none text-accion-acento">
              »
            </span>
          ) : null}
        </>
      )}
    </button>
  )
}

export function Campo({
  label,
  ayuda,
  error,
  children,
}: {
  label: string
  ayuda?: string
  error?: string
  children: ReactNode
}) {
  return (
    <div className="flex flex-col gap-1.5">
      {/* Etiqueta visible siempre, no solo como marcador de posición: el
          marcador desaparece al escribir y deja el campo sin identificar. */}
      <label className="text-[13px] font-medium text-ink">{label}</label>
      {children}
      {error ? (
        // El error va pegado a su campo, no agrupado arriba: buscar a cuál
        // corresponde cada mensaje es trabajo que el formulario debe ahorrar.
        <p role="alert" className="text-[12px] text-expense-text">
          {error}
        </p>
      ) : ayuda ? (
        <p className="text-[12px] leading-snug text-ink-faint">{ayuda}</p>
      ) : null}
    </div>
  )
}

export function Texto({
  value,
  onChange,
  placeholder,
  type = 'text',
  inputMode,
  maxLength,
  invalido = false,
}: {
  value: string
  onChange: (v: string) => void
  placeholder?: string
  type?: string
  inputMode?: 'text' | 'numeric' | 'decimal' | 'email' | 'tel'
  maxLength?: number
  invalido?: boolean
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      inputMode={inputMode}
      maxLength={maxLength}
      aria-invalid={invalido}
      // 16px evita que iOS acerque el zoom al enfocar, un salto que descoloca
      // toda la pantalla.
      className={cn(
        'min-h-[48px] w-full rounded-card border bg-surface px-3.5 text-[16px] text-ink',
        'placeholder:text-ink-faint',
        invalido ? 'border-expense' : 'border-line',
      )}
    />
  )
}

/** Monto con el teclado propio de la app, el mismo de la captura de gastos. */
export function Monto({
  cents,
  onChange,
}: {
  cents: number
  onChange: React.Dispatch<React.SetStateAction<number>>
}) {
  return (
    <div className="flex flex-col gap-2">
      <AmountDisplay cents={cents} />
      <NumPad valueCents={cents} onChange={onChange} />
    </div>
  )
}

export interface OpcionSegmento<T extends string> {
  value: T
  label: string
  detalle?: string
}

/** Selector de opciones excluyentes, en rejilla. */
export function Segmentos<T extends string>({
  opciones,
  value,
  onChange,
  columnas = 2,
}: {
  opciones: ReadonlyArray<OpcionSegmento<T>>
  value: T
  onChange: (v: T) => void
  columnas?: 2 | 3
}) {
  return (
    <div className={cn('grid gap-2', columnas === 3 ? 'grid-cols-3' : 'grid-cols-2')}>
      {opciones.map((o) => {
        const activo = o.value === value
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => {
              haptic('selection')
              onChange(o.value)
            }}
            aria-pressed={activo}
            className={cn(
              'flex min-h-[52px] flex-col items-start justify-center gap-0.5 rounded-card border px-3 py-2 text-left',
              'transition-colors duration-150 active:scale-[0.98]',
              activo ? 'border-primary bg-primary-soft' : 'border-line bg-surface',
            )}
          >
            <span className={cn('text-[14px] font-semibold', activo ? 'text-ink' : 'text-ink-soft')}>
              {o.label}
            </span>
            {o.detalle ? (
              <span className="text-[11px] leading-tight text-ink-faint">{o.detalle}</span>
            ) : null}
          </button>
        )
      })}
    </div>
  )
}

export function Interruptor({
  label,
  ayuda,
  value,
  onChange,
}: {
  label: string
  ayuda?: string
  value: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={value}
      onClick={() => {
        haptic('selection')
        onChange(!value)
      }}
      className="flex min-h-[52px] w-full items-center gap-3 rounded-card border border-line bg-surface px-3.5 py-2.5 text-left active:scale-[0.99]"
    >
      <span className="min-w-0 flex-1">
        <span className="block text-[14px] font-medium text-ink">{label}</span>
        {ayuda ? (
          <span className="block text-[12px] leading-snug text-ink-soft">{ayuda}</span>
        ) : null}
      </span>
      <span
        className={cn(
          'relative h-6 w-10 shrink-0 rounded-pill transition-colors duration-200',
          value ? 'bg-primary' : 'bg-surface-sunken',
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 size-5 rounded-full bg-white shadow-sm transition-transform duration-200',
            value ? 'translate-x-[1.125rem]' : 'translate-x-0.5',
          )}
        />
      </span>
    </button>
  )
}

export function AvisoError({ mensaje }: { mensaje: string | null }) {
  if (!mensaje) return null
  return (
    <p role="alert" className="rounded-card bg-expense-soft px-3.5 py-2.5 text-[13px] text-expense-text">
      {mensaje}
    </p>
  )
}
